import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Send, Facebook, Instagram, Music2 } from "lucide-react";
import NTPLogo from "ntp-logo-react";
import { toast } from "sonner";
import { LogoWhite } from "./Logo";
import { api } from "../lib/api";

const SOCIALS = [
  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/detergentzilnic" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/detergentzilnic" },
  { icon: Music2, label: "TikTok", href: "https://www.tiktok.com/@detergentzilnic" },
];

export function Footer() {
  const [content, setContent] = useState({});
  const [newsletter, setNewsletter] = useState("");

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data || {})).catch(() => {});
  }, []);

  const subscribe = (e) => {
    e.preventDefault();
    if (!newsletter.includes("@")) {
      toast.error("Introdu o adresă de email validă");
      return;
    }
    toast.success("Te-ai abonat cu succes la noutăți");
    setNewsletter("");
  };

  return (
    <footer className="bg-navy text-white" data-testid="site-footer">
      <div className="mx-auto max-w-[1120px] px-5 py-16 sm:px-8">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <LogoWhite className="h-8 w-auto" />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-sky-light">
              {content.footer_about || "zilnic — detergent de rufe simplu și la îndemână. Un produs marca Detergent Studio SRL."}
            </p>
            <div className="mt-4 max-w-sm text-xs leading-relaxed text-sky-light/80" data-testid="footer-company-info">
              <p className="font-bold text-white">{content.company_name || "Detergent Studio SRL"}</p>
              {(content.company_cui || content.company_reg) && (
                <p>
                  {content.company_cui && `CUI: ${content.company_cui}`}
                  {content.company_cui && content.company_reg && " · "}
                  {content.company_reg && `Nr. Reg. Com.: ${content.company_reg}`}
                </p>
              )}
              {content.company_address && <p>{content.company_address}</p>}
            </div>
            <form onSubmit={subscribe} className="mt-6 flex max-w-sm overflow-hidden rounded-lg border border-white/20 bg-white/10 p-1" data-testid="newsletter-form">
              <input
                type="email"
                value={newsletter}
                onChange={(e) => setNewsletter(e.target.value)}
                placeholder="Email pentru noutăți"
                className="w-full bg-transparent px-4 text-sm text-white placeholder:text-sky-light/70 focus:outline-none"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white text-navy transition-colors hover:bg-skytint"
                aria-label="Abonează-te"
                data-testid="newsletter-submit-button"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
            <div className="mt-6 flex gap-2.5">
              {SOCIALS.map((s) => ({ ...s, href: content[`social_${s.label.toLowerCase()}`] || s.href })).map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-sky-light transition-colors hover:border-white hover:text-white"
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                >
                  <s.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
                </a>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.08em] text-white">Navigare</h4>
            <ul className="mt-4 flex flex-col gap-2.5 text-sm text-sky-light">
              <li><Link to="/" className="transition-colors hover:text-white">Acasă</Link></li>
              <li><Link to="/produse" className="transition-colors hover:text-white">Produse</Link></li>
              <li><Link to="/produse?promo=1" className="transition-colors hover:text-white">Promoții</Link></li>
              <li><Link to="/#cum-se-foloseste" className="transition-colors hover:text-white">Cum se folosește</Link></li>
              <li><Link to="/#pentru-magazine" className="transition-colors hover:text-white">Pentru magazine</Link></li>
              <li><Link to="/cont" className="transition-colors hover:text-white">Contul meu</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-xs font-bold uppercase tracking-[0.08em] text-white">Legal</h4>
            <ul className="mt-4 flex flex-col gap-2.5 text-sm text-sky-light">
              <li><Link to="/legal#termeni" className="transition-colors hover:text-white" data-testid="footer-legal-termeni">Termeni și condiții</Link></li>
              <li><Link to="/legal#livrare" className="transition-colors hover:text-white" data-testid="footer-legal-livrare">Politica de livrare</Link></li>
              <li><Link to="/legal#anulare" className="transition-colors hover:text-white" data-testid="footer-legal-anulare">Politica de anulare</Link></li>
              <li><Link to="/legal#retur" className="transition-colors hover:text-white" data-testid="footer-legal-retur">Politica de retur</Link></li>
              <li><Link to="/retragere" className="transition-colors hover:text-white" data-testid="footer-legal-retragere">Retrage-te din contract aici</Link></li>
              <li><Link to="/legal#anpc" className="transition-colors hover:text-white" data-testid="footer-legal-anpc">ANPC — SAL / SOL</Link></li>
              <li><Link to="/legal#gdpr" className="transition-colors hover:text-white" data-testid="footer-legal-gdpr">Confidențialitate (GDPR)</Link></li>
              <li><Link to="/legal#cookies" className="transition-colors hover:text-white" data-testid="footer-legal-cookies">Politica de cookie-uri</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-xs font-bold uppercase tracking-[0.08em] text-white">Contact</h4>
            <ul className="mt-4 flex flex-col gap-3 text-sm text-sky-light">
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 text-sky-light" />
                <a href={`https://wa.me/${(content.contact_phone || "+40 721 000 000").replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white hover:underline" data-testid="footer-phone">{content.contact_phone || "+40 721 000 000"}</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-sky-light" />
                <a href={`mailto:${content.contact_email || "contact@detergentzilnic.ro"}`} className="transition-colors hover:text-white hover:underline" data-testid="footer-email">{content.contact_email || "contact@detergentzilnic.ro"}</a>
              </li>
              <li className="flex items-center gap-2.5">
                <MapPin className="h-4 w-4 text-sky-light" />
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(content.contact_address || "București, România")}`} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white hover:underline" data-testid="footer-address">{content.contact_address || "București, România"}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3" data-testid="footer-compliance">
          <a
            href="https://reclamatiisal.anpc.ro"
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-[50px] w-[250px] items-center justify-center rounded-lg bg-white px-4 text-center text-xs font-bold leading-tight text-navy transition-colors hover:bg-skytint"
            data-testid="footer-badge-sal"
          >
            ANPC — Soluționarea alternativă a litigiilor
          </a>
          <a
            href="https://ec.europa.eu/consumers/odr"
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-[50px] w-[250px] items-center justify-center rounded-lg bg-white px-4 text-center text-xs font-bold leading-tight text-navy transition-colors hover:bg-skytint"
            data-testid="footer-badge-sol"
          >
            Soluționarea online a litigiilor
          </a>
          <div className="flex items-center rounded-lg px-4 py-1.5" data-testid="footer-netopia-badge">
            <NTPLogo color="#1B4B7A" version="horizontal" secret="170070" />
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-white/15 pt-7 text-xs text-sky-light/80 sm:flex-row">
          <p>© {new Date().getFullYear()} detergentzilnic.ro — Un produs marca Detergent Studio SRL.</p>
          <p>curat, simplu, la îndemână</p>
        </div>
      </div>
    </footer>
  );
}
